<template>
  <!-- Estructura común: Comunidades y CUPS de luz/gas, con el alcance de cada rol. -->
  <div v-if="showPortfolioTabs" class="flex flex-wrap gap-2 pb-6" role="tablist">
    <button v-for="tab in portfolioTabs" :key="tab.key" type="button" role="tab"
      :aria-selected="activeTab === tab.key"
      class="py-2 px-4 rounded-lg shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] cursor-pointer transition-colors duration-300 text-nowrap"
      :class="activeTab === tab.key ? 'bg-[#2AB4D9] text-white' : 'bg-white text-[#385660] hover:text-[#2AB4D9]'"
      @click="selectTab(tab.key)">
      {{ tab.label }}
    </button>
  </div>

  <ClientSupplyTable v-if="activeTab !== 'communities'" :type="activeTab" :title="activeTabLabel" :property-type="propertyType" />

  <template v-else>
  <div class="flex flex-col-reverse md:flex-row pb-6 justify-between gap-4">
    <div class="flex w-full md:w-auto justify-between gap-0 md:gap-4">
      <ExportFetch exportEndpoint="/property/user/export" :fileName="fileName"
        :property_type="propertyType" :exportType="propertyType || 'both'" :extraParams="exportParams"
        :date-range="internalUser && !route.query?.id"
        :from-label="$t('export.range.createdFrom')" :to-label="$t('export.range.createdTo')"
        :selection="internalUser ? selection : null"
        @export-started="handleExportStarted" @export-completed="handleExportCompleted"
        @export-failed="handleExportFailed" />

      <PrintTableComponent :title="$t('property.title')" :fields="translatedFields" :items="items"
        @print-started="() => console.log($t('property.logs.printStarted'))"
        @print-completed="() => console.log($t('property.logs.printCompleted'))"
        @print-failed="(error) => console.error($t('property.logs.printFailed'), error)" />
    </div>

    <div class="flex flex-col-reverse md:flex-row gap-2 w-full md:w-auto justify-between  md:gap-4">
      <div class="flex flex-row gap-2 w-full md:w-auto justify-between md:gap-4">
          <!-- Todos los perfiles ADS dan de alta en la cuenta que elijan (ADSREC-0006) -->
          <RouterLink v-if="internalUser
            || (propertyType === 'inmueble' && (hasRole('vertical') || hasRole('both')))
            || (propertyType === 'comunidad' && (hasRole('horizontal') || hasRole('both') || hasRole('worker')))"
            :to="{ path: '/Property-new', query: { type: propertyType } }">
            <ButtonWhite class="flex items-center gap-2 text-[#385660] cursor-pointer ">
            {{ $t('property.buttons.addNew') }}
            <PlusIcon />
          </ButtonWhite>
        </RouterLink>
      </div>
      
    </div>
  </div>

  <TitlePrimary :title="propertyType === 'inmueble' ? $t('property.tabs.estates') : $t('user.properties.title')" class="w-full" />
  <div v-if="route.query && route.query.id" class="flex flex-col mt-2 flex items-center gap-2">
    <ButtonWhite class="flex items-center gap-2" @click="removeIdParam">{{ t('common.backToSearch') || 'Volver a búsqueda normal' }}</ButtonWhite>
    <div class="text-sm text-[#385660]">{{ noCommunitiesForAccount }}</div>
  </div>

  <div class="pt-6 md:pt-0 md:hidden">
  <CardTable v-if="hasRole('superadmin')" :fields="translatedFields"
      :items="items.length > 0 ? [items[currentItemIndex]] : []" :showOptions="false" route="Property-detail"
      :listOptions="translatedOptions" />
    <CardTable v-else :fields="translatedFields" :items="items.length > 0 ? [items[currentItemIndex]] : []"
      :showOptions="false" route="Property-detail" :listOptions="translatedOptions" />
    <MobilePaginateComponent v-if="items.length > 0" :totalPages="totalPages" :modelValue="currentPage"
      :totalItems="totalItems" :currentItemIndex="currentItemIndex" :itemsPerPage="10"
      @update:modelValue="handlePageChange" @update:currentItemIndex="handleItemChange" />
  </div>

  <div class="hidden md:block">
    <TableComponent :filter-definitions="filterDefinitions" :filter-error="filterError" v-model:filters="filters" @filter-change="onFilterChange" :fields="translatedFields" :items="items" :showViewMoreButton="true" route="Property-detail"
      :listOptions="[]" :selection="internalUser ? selection : null" />

    <PaginateComponent :totalPages="totalPages" :paginationInfo="paginationInfo" :modelValue="currentPage" @update:modelValue="handlePageChange" />
  </div>
  </template>
</template>

<script setup>
import { ref, onMounted, computed, reactive, watch } from "vue";
import axios from "axios";
import { useI18n } from 'vue-i18n';
import { useExportStore } from '@/stores/exportStore';
import { useRoute, useRouter } from 'vue-router';
import { useTableFilters } from '@/composables/useTableFilters';
import { useRowSelection } from '@/composables/useRowSelection';

import TableComponent from "@/components/table/TableComponent.vue";
import PaginateComponent from "@/components/paginate/PaginateComponent.vue";
import { usePaginationInfo } from "@/composables/usePaginationInfo";
import ButtonWhite from "@/components/button/ButtonWhite.vue";
import ExportFetch from "@/components/fetch/exportFetch.vue";
import PrintTableComponent from "@/components/print/PrintTableComponent.vue";

import PlusIcon from "@/assets/svg/PlusIcon.vue";
import TitlePrimary from "@/components/title/title-primary.vue";
import CardTable from '@/components/table/CardTable.vue';
import MobilePaginateComponent from '@/components/paginate/MobilePaginateComponent.vue';
import ClientSupplyTable from '@/components/supply/ClientSupplyTable.vue';
import { isAdsUser } from '@/services/userRoles';
import { estateHolderName } from '@/services/estateHolder';


// --- Estado ---
const items = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const { paginationInfo, setPaginationInfo } = usePaginationInfo();
const { t } = useI18n();

// Nombre de archivo para export dependiendo del tipo de propiedad
const fileName = computed(() => {
  if (propertyType.value === 'inmueble') {
    return t('menu.estates');
  }
  return t('property.fileName');
});

// Permitir que role sea string o array (ej: '["vertical","horizontal"]')
let storedRole = localStorage.getItem('role');
let parsedRole;
try {
  parsedRole = JSON.parse(storedRole);
} catch {
  parsedRole = storedRole;
}
const role = ref(parsedRole);
const internalUser = isAdsUser();

function hasRole(target) {
  if (Array.isArray(role.value)) {
    return role.value.includes(target);
  }
  return role.value === target;
}
const exportStore = useExportStore();

const search = ref(false);

const searchText = ref('');
const selectedFilterName = ref({ key: '', label: '' });


const route = useRoute();
const router = useRouter();
const propertyType = computed(() => {
  const type = route.query.type;
  if (type === 'comunidad' || type === 'inmueble') {
    return type;
  }
  return '';
});
const currentItemIndex = ref(0);
const totalItems = ref(0);
// Mensaje cuando no hay comunidades para la cuenta (respuesta 404 específica)
const noCommunitiesForAccount = ref('');

// Comunidades e Inmuebles llevan las mismas tres pestañas (ADS-0123): la
// cartera y sus CUPS de luz y de gas, cada apartado con los de sus fincas.
const SUPPLY_TABS = ['light', 'gas'];
const showPortfolioTabs = computed(() => propertyType.value === 'comunidad' || propertyType.value === 'inmueble');
const activeTab = computed(() => (showPortfolioTabs.value && SUPPLY_TABS.includes(route.query?.tab) ? route.query.tab : 'communities'));
const portfolioTabs = computed(() => [
  { key: 'communities', label: t(propertyType.value === 'inmueble' ? 'property.tabs.estates' : 'property.tabs.communities') },
  { key: 'light', label: t('property.tabs.light') },
  { key: 'gas', label: t('property.tabs.gas') },
]);
const activeTabLabel = computed(() => portfolioTabs.value.find((tab) => tab.key === activeTab.value)?.label || '');

function selectTab(key) {
  const query = { ...route.query };
  if (key === 'communities') {
    delete query.tab;
  } else {
    query.tab = key;
  }
  router.replace({ path: route.path, query }).catch(() => {});
}

const translatedFields = computed(() => {
  const fields = [];
  // ADS ve la cartera de muchas cuentas: la columna dice de cuál es cada registro (ADSREC-0006).
  if (internalUser) {
    fields.push({ key: "cliente", label: t('property.communityForm.fields.account') });
  }

  // Comunidades e Inmuebles comparten columnas (ADS-0123, ADS-0170): titular,
  // documento y trazabilidad de origen. El titular, no el nombre de la finca,
  // que en los inmuebles migrados es la dirección (ADS-0169).
  fields.push(
    { key: "holder", label: t('property.table.supplyHolder') },
    { key: "cif", label: t('property.table.document') },
    // Dirección y municipio ocultos a pedido del cliente (16/09/2026): la ubicación se consulta por CUPS en las pestañas de luz y gas.
    { key: "created_at", label: t('property.table.createdAt') },
    // Misma clave que el filtro de cabecera (origin) para que el desplegable ADS/Cliente se pinte en la columna
    { key: "origin", label: t('property.table.createdBy') },
    { key: "opciones", label: t('property.table.options') }
  );

  return fields;
});

// --- filtros de cabecera ---
const filterDefinitions = computed(() => {
  const definitions = [];

  if (internalUser) {
    // La columna del administrador se filtra por el nombre de la cuenta.
    definitions.push({ key: 'cliente', type: 'text' });
  }

  definitions.push(
    { key: 'holder', type: 'text' },
    { key: 'cif', type: 'text' },
    { key: 'created_at', type: 'date' },
    { key: 'origin', type: 'select', options: ['ads', 'client'].map((value) => ({ value, label: t(`property.origin.${value}`) })) },
  );

  return definitions;
});

const { filters, activeFilters, isFiltering, filterError, runSearch, onFilterChange } = useTableFilters({
  endpoint: () => (hasRole('superadmin') && !route.query?.id ? '/property/superadmin/search' : '/property/user/search'),
  perPage: 10,
  translate: t,
  extraParams: () => ({
    property_type: propertyType.value,
    ...(route.query?.id ? { id: route.query.id } : {}),
  }),
  onResults: (data) => {
    const page = data?.value;
    if (!page) return;
    items.value = (page.data || []).map(mapPropertyRow);
    totalPages.value = page.last_page || 1;
    setPaginationInfo(page);
    totalItems.value = page.total ?? 0;
    currentPage.value = page.current_page || 1;
    search.value = true;
    noCommunitiesForAccount.value = '';
  },
  onCleared: () => {
    search.value = false;
    currentPage.value = 1;
    fetchDataFromApi();
  },
});

// ADS elige qué exportar con casillas: filas sueltas o todo lo filtrado
// (ADSREC-0029). «Todo» se suelta al cambiar los filtros; otra cartera
// (Comunidades ↔ Inmuebles, otra cuenta) empieza sin nada marcado.
const selection = reactive(useRowSelection(totalItems));
watch(activeFilters, () => selection.releaseAll());
watch(() => [propertyType.value, route.query?.id], () => selection.clear());

// La exportación baja lo que muestra el listado: la cuenta abierta desde su
// ficha y los filtros de cabecera (ADS-0186). ADS, fuera de la ficha de una
// cuenta, exporta además por fecha de creación, un año como máximo.
const exportParams = computed(() => ({
  ...(route.query?.id ? { id: route.query.id } : {}),
  ...Object.fromEntries(Object.entries(activeFilters.value).map(([field, value]) => [`filters[${field}]`, value])),
}));

// Fila del listado: la cuenta, la fecha de creación real y quién creó el
// registro (los migrados y la carga masiva cuentan como ADS).
const formatDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '-';
};
const createdByLabel = (item) => {
  const origin = t(`property.origin.${item.origin === 'client' ? 'client' : 'ads'}`);
  const creator = [item.creator?.name, item.creator?.surname].filter(Boolean).join(' ');
  return creator ? `${origin} · ${creator}` : origin;
};
const mapPropertyRow = (item) => ({
  ...item,
  cliente: item.propietario?.username || item.propietario?.name || '',
  holder: estateHolderName(item),
  created_at: formatDate(item.created_at),
  origin: createdByLabel(item),
});

const translatedOptions = computed(() => [
  { key: '/forms/supplies', label: t('property.options.supplyRegistration') },
  { key: '/forms/account_change', label: t('property.options.accountHolderChange') },
  { key: '/forms/change_ownership', label: t('property.options.supplyAccountChange') },
  // Retirada «Cambio de comercializadora» (ADS-0130): se deja comentada, no borrada, por si hubiera que reponerla.
  // { key: '/forms/change_supplier', label: t('property.options.supplierChange') },
  { key: '/forms/invoice', label: t('property.options.invoiceRequest') },
  { key: '/forms/other_type', label: t('property.options.otherType') }
]);



// --- funciones ---
const fetchDataFromApi = async () => {
  try {
    // If an `id` is present in the route query, always call the user list endpoint
    const endpoint = route.query && route.query.id
      ? '/property/user/list'
      : (role.value === 'superadmin' ? '/property/superadmin/list' : '/property/user/list');

    const params = {
      per_page: 10,
      page: currentPage.value,
      type: propertyType.value || null,
    };

    if (route.query && route.query.id) {
      params.id = route.query.id;
    }

    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${endpoint}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params,
      }
    );

    if (response.data?.value.data) {
      items.value = response.data.value.data.map(mapPropertyRow);
      totalPages.value = response.data.value.last_page;
      setPaginationInfo(response.data.value);
      totalItems.value = response.data.value.total ?? 0;
      // limpiar mensaje si hubo uno previamente
      noCommunitiesForAccount.value = '';
    }
  } catch (error) {
    items.value = [];
    if (error.response && error.response.status === 404) {
      const respValue = error.response.data?.value || error.response.data?.message || '';
      const isNoPropertiesMsg = typeof respValue === 'string' && (
        respValue.includes('No properties found') || respValue.includes('No properties found for this user')
      );
      if (isNoPropertiesMsg) {
        noCommunitiesForAccount.value = 'no hay comunidades para esa cuenta';
      } else {
        console.error("No se encontraron resultados para el tipo seleccionado.");
      }
    } else {
      console.error("Error fetching data:", error);
    }
  }
};

// watch(currentPage, (newPage) => {
//   fetchDataFromApi();
// });


// Manejo de cambio de página
const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  if (isFiltering.value) {
    await runSearch(newPage);
  } else {
    fetchDataFromApi();
  }
};

const handleItemChange = (newIndex) => {
  if (newIndex >= 0 && newIndex < items.value.length) {
    currentItemIndex.value = newIndex;
  }
};

async function performSearch(searchEndpoint, perPage, page = 1) {
  try {
    const params = {
      query: searchText.value,
      per_page: perPage,
      page: page,
      search_by: selectedFilterName.value.key,
      type: propertyType.value || null,
    };

    // Si la ruta tiene `id`, añadirla a los parámetros de la petición
    if (route && route.query && route.query.id) {
      params.id = route.query.id;
    }

    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${searchEndpoint}`,
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    console.error('search-error', error);
  }
}
defineExpose({ performSearch });

onMounted(() => {
  fetchDataFromApi();
});

// --- Funciones para los iconos ---
// const handleEdit = (item) => {
//   console.log('Editar:', item);
// };

// const handleAdd = (item) => {
//   console.log('Añadir/Otra acción:', item);
// };

// Añadir las funciones de exportación
const handleExportStarted = () => {
  console.log(t('property.logs.exportStarted'));
  exportStore.startExport('Exportando comunidades...');
};

const handleExportCompleted = () => {
  console.log(t('property.logs.exportCompleted'));
  exportStore.completeExport();
};

const handleExportFailed = () => {
  console.log(t('property.logs.exportFailed'));
  exportStore.failExport('Error al exportar comunidades');
};

function removeIdParam() {
  const newQuery = { ...route.query };
  if (newQuery && newQuery.id) {
    delete newQuery.id;
    router.replace({ path: route.path, query: newQuery }).catch(() => {});
  }
  noCommunitiesForAccount.value = '';
  // recargar lista sin el id
  fetchDataFromApi();
}

</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
